import React, { useState, useEffect, useRef } from 'react';
import { X, Trash2, Bug } from 'lucide-react';

interface LogEntry {
    type: 'log' | 'warn' | 'error' | 'info';
    message: string;
    timestamp: string;
}

const MobileConsole: React.FC = () => {
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Save original methods
        const originalLog = console.log;
        const originalWarn = console.warn;
        const originalError = console.error;
        const originalInfo = console.info;

        const addLog = (type: LogEntry['type'], args: any[]) => {
            const message = args.map(arg => {
                if (typeof arg === 'object') {
                    try {
                        return JSON.stringify(arg, null, 2);
                    } catch (e) {
                        return String(arg);
                    }
                }
                return String(arg);
            }).join(' ');

            const newLog: LogEntry = {
                type,
                message,
                timestamp: new Date().toLocaleTimeString()
            };

            setLogs(prev => [...prev.slice(-99), newLog]); // Keep last 100 logs
        };

        console.log = (...args) => {
            originalLog(...args);
            addLog('log', args);
        };

        console.warn = (...args) => {
            originalWarn(...args);
            addLog('warn', args);
        };

        console.error = (...args) => {
            originalError(...args);
            addLog('error', args);
        };

        console.info = (...args) => {
            originalInfo(...args);
            addLog('info', args);
        }

        // Capture global errors
        const handleError = (event: ErrorEvent) => {
            addLog('error', [`Uncaught Error: ${event.message}`, `at ${event.filename}:${event.lineno}:${event.colno}`]);
        };

        const handleRejection = (event: PromiseRejectionEvent) => {
            addLog('error', ['Unhandled Promise Rejection:', event.reason]);
        }

        window.addEventListener('error', handleError);
        window.addEventListener('unhandledrejection', handleRejection);

        return () => {
            console.log = originalLog;
            console.warn = originalWarn;
            console.error = originalError;
            console.info = originalInfo;
            window.removeEventListener('error', handleError);
            window.removeEventListener('unhandledrejection', handleRejection);
        };
    }, []);

    // Auto-scroll to bottom
    useEffect(() => {
        if (isOpen && bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs, isOpen]);

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="fixed bottom-20 right-4 z-[9999] p-3 bg-gray-900 text-white rounded-full shadow-lg opacity-70 hover:opacity-100 transition-opacity border border-gray-700"
                title="Open Mobile Console"
            >
                <Bug className="w-6 h-6 text-green-400" />
            </button>
        );
    }

    return (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-95 text-green-400 font-mono text-xs flex flex-col animate-in fade-in duration-200">
            <div className="flex items-center justify-between p-3 bg-gray-900 border-b border-gray-700 shadow-md">
                <div className="flex items-center gap-2">
                    <Bug className="w-4 h-4" />
                    <span className="font-bold text-sm">Mobile Console ({logs.length})</span>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => setLogs([])}
                        className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
                        title="Clear Logs"
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="p-2 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
                        title="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-gray-700">
                {logs.map((log, index) => (
                    <div key={index} className={`border-b border-gray-800 pb-1 mb-1 ${log.type === 'error' ? 'text-red-400 bg-red-900/10 p-1 rounded' :
                            log.type === 'warn' ? 'text-yellow-400 bg-yellow-900/10 p-1 rounded' : 'p-1'
                        }`}>
                        <div className="flex gap-2 opacity-70 mb-0.5">
                            <span>[{log.timestamp}]</span>
                            <span className="font-bold uppercase">{log.type}</span>
                        </div>
                        <div className="whitespace-pre-wrap break-words pl-2 border-l-2 border-gray-700">
                            {log.message}
                        </div>
                    </div>
                ))}
                {logs.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-full text-gray-600 space-y-2">
                        <Bug className="w-12 h-12 opacity-20" />
                        <p className="italic">No logs captured yet...</p>
                        <p className="text-[10px] opacity-50">Interact with the app to see events</p>
                    </div>
                )}
                <div ref={bottomRef} />
            </div>
        </div>
    );
};

export default MobileConsole;
